import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { EventForReports } from '../../../shared/interfaces';

const currencyFormatter = new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const numberFormatter = new Intl.NumberFormat('es-CO');

export function formatCurrency(value: number | null | undefined): string {
  return currencyFormatter.format(value ?? 0);
}

/** $4,3 M · $850 mil · $900 — para ejes y textos cortos */
export function formatCompactCurrency(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `$${(value / 1_000_000).toLocaleString('es-CO', { maximumFractionDigits: 1 })} M`;
  if (abs >= 1_000) return `$${Math.round(value / 1_000).toLocaleString('es-CO')} mil`;
  return `$${Math.round(value).toLocaleString('es-CO')}`;
}

export function formatNumber(value: number | null | undefined): string {
  return numberFormatter.format(value ?? 0);
}

export function formatPercent(value: number | null | undefined, digits = 0): string {
  if (value === null || value === undefined) return '—';
  return `${value.toLocaleString('es-CO', { maximumFractionDigits: digits })} %`;
}

export function formatTrend(value: number | null): string {
  if (value === null) return 'sin base';
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${sign}${Math.abs(value).toLocaleString('es-CO', { maximumFractionDigits: 1 })} %`;
}

export function formatRange(start: Date, end: Date): string {
  const sameYear = start.getFullYear() === end.getFullYear();
  if (format(start, 'yyyy-MM-dd') === format(end, 'yyyy-MM-dd')) {
    return format(start, "d 'de' MMMM yyyy", { locale: es });
  }
  const startFmt = sameYear ? "d 'de' MMM" : "d 'de' MMM yyyy";
  return `${format(start, startFmt, { locale: es })} al ${format(end, "d 'de' MMM yyyy", { locale: es })}`;
}

export type StatusTone = 'done' | 'upcoming' | 'lost';

export interface StatusMeta {
  label: string;
  color: string;
  tone: StatusTone;
  order: number;
}

/** Colores de estado reservados: siempre acompañados de su etiqueta, nunca solos. */
export const STATUS_META: Record<string, StatusMeta> = {
  completed: { label: 'Completadas', color: '#12b76a', tone: 'done', order: 0 },
  confirmed: { label: 'Confirmadas', color: '#465fff', tone: 'upcoming', order: 1 },
  scheduled: { label: 'Agendadas', color: '#f79009', tone: 'upcoming', order: 2 },
  pending_client: { label: 'Por aprobar', color: '#7a5af8', tone: 'upcoming', order: 3 },
  cancelled: { label: 'Canceladas', color: '#f04438', tone: 'lost', order: 4 },
  no_show: { label: 'No asistió', color: '#667085', tone: 'lost', order: 5 },
};

/** Estados que se pueden elegir en el filtro (los que existen en la agenda) */
export const FILTERABLE_STATUSES = ['scheduled', 'pending_client', 'confirmed', 'completed', 'cancelled'];

export function statusMeta(code: string, fallbackLabel?: string): StatusMeta {
  return STATUS_META[code] ?? { label: fallbackLabel || code, color: '#98a2b3', tone: 'upcoming', order: 9 };
}

/** Lo que se cobró (o se cobrará) por la cita: precio final, o precio del servicio menos la promoción. */
export function eventPrice(e: EventForReports): number {
  if (e.finalPrice !== null && e.finalPrice !== undefined) return Number(e.finalPrice);
  const price = Number(e.servicePrice ?? 0);
  const discount = Number(e.promotionAppliedAmount ?? 0);
  return Math.max(0, price - discount);
}

export function serviceLabel(e: EventForReports): string {
  const name = e.serviceName || 'Sin servicio';
  if (e.totalSessions && e.totalSessions > 1) {
    return e.sessionNumber
      ? `${name} (sesión ${e.sessionNumber} de ${e.totalSessions})`
      : `${name} (${e.totalSessions} sesiones)`;
  }
  return name;
}

export function professionalLabel(e: EventForReports): string {
  return e.professionalName || e.agendaName || 'Sin asignar';
}

export const PAYMENT_METHOD_LABELS: Record<string, string> = {
  cash: 'Efectivo',
  card: 'Tarjeta',
  transfer: 'Transferencia',
  epayco_link: 'Link de pago',
  manual: 'Manual',
  credit: 'Crédito',
};

export const WEEKDAY_LABELS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
